import React, { useEffect, useState } from 'react';

// Minimal client-side router. Azure Static Web Apps falls back to index.html
// for unknown paths (see public/staticwebapp.config.json), so /about etc. load the app.

const NAV_EVENT = 'app:navigate';

export const navigate = (to: string) => {
  const url = new URL(to, window.location.origin);
  const current = window.location.pathname + window.location.hash;
  if (url.pathname + url.hash !== current) {
    window.history.pushState({}, '', url.pathname + url.hash);
  }
  window.dispatchEvent(new Event(NAV_EVENT));
};

export const useLocation = () => {
  const read = () => ({ path: normalize(window.location.pathname), hash: window.location.hash, key: Date.now() });
  const [location, setLocation] = useState(read);

  useEffect(() => {
    const update = () => setLocation(read());
    window.addEventListener('popstate', update);
    window.addEventListener(NAV_EVENT, update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener(NAV_EVENT, update);
    };
  }, []);

  return location;
};

const normalize = (path: string) => (path.length > 1 ? path.replace(/\/+$/, '') : path);

interface LinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
}

export const Link: React.FC<LinkProps> = ({ to, onClick, children, ...rest }) => (
  <a
    href={to}
    onClick={(e) => {
      onClick?.(e);
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      navigate(to);
    }}
    {...rest}
  >
    {children}
  </a>
);

// Opens in a new tab — used for every external link.
export const ExternalLink: React.FC<React.AnchorHTMLAttributes<HTMLAnchorElement>> = ({ children, ...rest }) => (
  <a target="_blank" rel="noopener noreferrer" {...rest}>
    {children}
  </a>
);
